/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Material Dashboard 2 React.
 * You can add new color using this file.
 * You can customized the colors for the entire Material Dashboard 2 React using thie file.
 */

import { MAU } from "assets/theme/avocadoBrand";

// Hai sắc xanh bơ dẫn xuất từ MAU.skin (#1F4029) — giữ nguyên tông màu (hue
// 143°) và độ bão hoà, chỉ nâng độ sáng để đủ tương phản khi làm nền nút:
// - LA (#2E6042): xanh "lá" — vai trò TƯƠNG TÁC (nút, viền ô nhập đang focus,
//   checkbox, link). Tương phản với chữ trắng ~7.3:1.
// - LA_NHAT (#3A7852): chỉ dùng làm điểm bắt đầu gradient.
// Giá trị trùng XANH_LA trong avocadoBrand.js — giữ local để colors.js không
// vòng import; khi đổi màu tương tác sửa cả hai (hoặc import XANH_LA sau).
const XANH_LA = "#2E6042";
const XANH_LA_NHAT = "#3A7852";
const XANH_LA_DAM = "#245134";

const colors = {
  // Nền trang = màu giấy của thương hiệu (MAU.paper) thay vì xám xanh
  // #f0f2f5 mặc định. Card vẫn để trắng để phân lớp bằng độ ấm chứ không
  // chỉ bằng đổ bóng.
  background: {
    default: MAU.paper,
  },

  // Xám ấm (hue ~42°) thay cho xám xanh #7b809a, để chữ phụ không bị lệch
  // tông lạnh trên nền giấy. Tương phản trên nền trắng ~5.3:1.
  text: {
    main: "#6F6B60",
    focus: "#6F6B60",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  // "primary" = màu NHẬN DIỆN (vỏ bơ đậm): mảng nền thương hiệu, tiêu đề
  // nhấn. Không dùng cho nút hành động — đã có "info" đảm nhiệm.
  primary: {
    main: MAU.skin,
    focus: MAU.skinDeep,
  },

  secondary: {
    main: "#7A7568",
    focus: "#8C8779",
  },

  // "info" = màu HÀNH ĐỘNG. Material Dashboard dùng slot này cho hầu hết
  // tương tác (nút contained/gradient, viền focus, checkbox, radio, slider,
  // stepper, link hover), và các trang trong app cũng đang dùng color="info"
  // cho nút chính — nên đây mới là màu người dùng bấm vào nhiều nhất.
  info: {
    main: XANH_LA,
    focus: XANH_LA_DAM,
  },

  success: {
    main: "#4CAF50",
    focus: "#67bb6a",
  },

  warning: {
    main: "#fb8c00",
    focus: "#fc9d26",
  },

  error: {
    main: "#F44335",
    focus: "#f65f53",
  },

  light: {
    main: "#F1EDE3",
    focus: "#F1EDE3",
  },

  // Mực của thương hiệu (đen ánh xanh) thay cho xanh navy #344767 của
  // template — đây là màu của MỌI tiêu đề h1-h6 (xem base/typography.js).
  dark: {
    main: MAU.ink,
    focus: "#0F1610",
  },

  // Thang xám ấm: giữ nguyên độ sáng của thang Bootstrap gốc nhưng đổi tông
  // từ xanh lạnh sang ~42° để đường kẻ, viền, chữ phụ hoà với nền giấy.
  grey: {
    100: "#FAF8F3",
    200: "#F1EDE3",
    300: "#E2DCCC",
    400: "#D0C9B6",
    500: "#ABA490",
    600: "#78736A",
    700: "#4F4C45",
    800: "#38352F",
    900: "#23211C",
  },

  gradients: {
    primary: {
      main: XANH_LA,
      state: MAU.skinDeep,
    },

    secondary: {
      main: "#747062",
      state: "#514E43",
    },

    // Gradient của nút hành động: lá non → vỏ bơ.
    info: {
      main: XANH_LA_NHAT,
      state: MAU.skin,
    },

    success: {
      main: "#66BB6A",
      state: "#43A047",
    },

    warning: {
      main: "#FFA726",
      state: "#FB8C00",
    },

    error: {
      main: "#EF5350",
      state: "#E53935",
    },

    light: {
      main: "#F5F0E2",
      state: "#DED7C4",
    },

    dark: {
      main: "#2E3A28",
      state: "#141A11",
    },

    // Thương hiệu "trái bơ bổ đôi" (xem assets/theme/avocadoBrand.js) — màu
    // hạt bơ (vàng nâu), dùng làm điểm nhấn mục đang chọn trên nền xanh đậm
    // của Sidenav thay vì màu "info" mặc định của Material Dashboard.
    avocado: {
      main: MAU.pit,
      state: "#B08128",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  badgeColors: {
    primary: {
      background: "#CDD9C6",
      text: MAU.skin,
    },

    secondary: {
      background: "#DCD8CC",
      text: "#6C6659",
    },

    info: {
      background: "#C6DACD",
      text: "#1F5637",
    },

    success: {
      background: "#bce2be",
      text: "#339537",
    },

    warning: {
      background: "#ffd59f",
      text: "#c87000",
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#CFC8B7",
    },

    dark: {
      background: "#96A38F",
      text: MAU.ink,
    },
  },

  coloredShadows: {
    primary: MAU.skin,
    secondary: "#110e0e",
    info: XANH_LA,
    success: "#4caf4f",
    warning: "#ff9900",
    error: "#f44336",
    light: "#ABA490",
    dark: "#22301F",
  },

  inputBorderColor: "#D8D2C2",

  tabs: {
    indicator: { boxShadow: "#E4DFD2" },
  },
};

export default colors;
